import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { fetchAssistantChatGPT } from '../../services/chat.service';
import { v4 as uuidv4 } from "uuid";
import useSessionStore from '../../store/useSessionStore';
import { getClassProfileById, storeClassProfileJson } from '../../services/profile-class.service';
import useSnackbarStore from '../../store/snackbarStore';
import { useSwitchInstitution } from '../../hooks/useIntitution';
import { getAllSubjectAreasByUser } from '../../services/subject-area.service';
import { getAllGradeLevelsByUser } from '../../services/grade-levels.service';
import { getbygradearea } from '../../services/dba.service';


/////////////ENAD///////////////////////////

/**
 * hook para las opciones de seleccion multiple
 * @param {*} param0 
 * @returns 
 */
export const useUtilsSelectEnad = ({ config, methods, arrayFields }) => {
    const { config: configuration } = useSwitchInstitution({ config });
    const groupingType = methods.watch("groupingType");
    const { t } = useTranslation();
    const area = methods.watch("area");
    const { setError, clearErrors } = methods;
    const { remove, fields: fieldslevels } = arrayFields.fieldArraylevels;

    // Validaciones de los niveles segun su limites
    useEffect(() => {
        if (groupingType?.limit[0] > fieldslevels.length) setError("levelscount", {
            type: "manual",
            message: t(groupingType?.tipo === "Homogénea" ? "remember_to_select_a_level" : "remember_to_select_at_least_two_levels"),
        });
        else if (groupingType?.limit[1] < fieldslevels.length) setError("levelscount", {
            type: "manual",
            message: t("must_select_maximun", { max: groupingType?.limit[1] }),
        });
        else clearErrors("levelscount");
    }, [fieldslevels, groupingType, t, setError, clearErrors]);

    const options = useMemo(() => configuration?.options ?? [], [configuration?.options]);

    const optionsArea = useMemo(() => {
        return options?.map(option => ({ value: option.area, label: option.area }))
            .filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);
    }, [options]);

    const optionstype = useMemo(() => {
        return options.map(option => ({ ...option, value: option.tipo, label: option.tipo }))
            .filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);
    }, [options]);

    const optionsLevel = useMemo(() => {
        if (!area?.value) return [];
        if (!groupingType?.value) return [];
        return options
            .filter(option => option.area === area?.value)
            .filter(option => option.tipo === groupingType?.value)
            .map(option => ({ ...option, value: option.nivel, label: option.nivel }))
            .filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);
    }, [groupingType, area, options]);

    const resetlevels = useCallback(() => {
        const i = fieldslevels?.map((_, i) => i) ?? [];
        if (i.length) remove(i);
    }, [remove, fieldslevels]);

    return { optionsArea, optionstype, groupingType, optionsLevel, t, resetlevels };
}


/**
 * Este Hook agrpa las funcionalidades del paso 2
 * permitiiendo la interaccion con el LLM
 * @param {*} param0 
 * @returns 
 */
export const useFormGroupEnadStep2 = ({ methods, promptObjetivos, promptobjetivosmodificar, extractOptionsfn, arrayFields }) => {
    const [options, setOptions] = useState([]);
    const [prevObjective, setPrevObjective] = useState();
    const [haveNewOptions, setHaveNewOptions] = useState();
    const [headers, setHeaders] = useState([]);
    const [optionModify, setOptionModify] = useState(null);
    const { getValues, setValue, clearErrors } = methods;
    const { fields: objetivesOptions, remove } = arrayFields.fieldArrayObjetivesOptions

    const { user } = useSessionStore()

    const _onSuccess = useCallback(({ othres = {} } = {}) => (res) => {
        let _text = res?.messages?.[0]?.data?.content;
        let _opts = extractOptionsfn(_text);
        _opts = _opts.map((option, index) => ({ id: uuidv4(), edited: othres?.index === index || othres?.edited, option, response: _text }))
        setOptions(_opts);
        _opts = extractOptionsfn(_text, [/<headeroptions>(.*?)<\/headeroptions>/gs]);
        setHeaders([{ response: _text, headers: _opts }]);
        setOptionModify(null);
        //Se limpian los campos
        setValue("objectives", "");
        clearErrors("objectives");
        setValue("ideas", "");
        clearErrors("ideas");
        setHaveNewOptions(false);
    }, [extractOptionsfn, setValue, clearErrors]);

    const mutation = useMutation(fetchAssistantChatGPT, {
        onSuccess: _onSuccess(),
        onError: (err) => {
            setHeaders([]);
            setOptions([]);
            setOptionModify(null);
        },
    });
    const { t, i18n } = useTranslation();
    const validate = useCallback(async ({ typeprompt = "validar_objetivos", _promptObjetivos = promptObjetivos, onSuccess, forceObjective = false }) => {
        const dataForm = getValues();
        if (!dataForm?.objectives && !forceObjective) return methods?.setError("objectives", {
            type: "manual",
            message: t("campo_requerido"),
        });
        setPrevObjective((prev) => {
            if (forceObjective) return prev;
            return dataForm?.objectives !== prevObjective ? dataForm?.objectives : prev;
        });
        const payload = {
            messages: [{ role: "user", content: _promptObjetivos({ user, data: { ...dataForm, objectives: forceObjective ? prevObjective : dataForm?.objectives } }) }],
            type: typeprompt,
            session_id: uuidv4(),
            lang: i18n.language,
            class_profile_id: null,
            asset_ids: [],
            response_type: null,
            response_schema: null,
        };
        // // mutationexample.mutation(payload);
        mutation.mutate(payload, { onSuccess });
        // eslint-disable-next-line
    }, [promptObjetivos, i18n, user, getValues, methods.setError, prevObjective]);

    const onClickGenerateNewOptions = useCallback(() => {
        validate({ typeprompt: "opcion_diferente_objetivos", _promptObjetivos: promptObjetivos, forceObjective: true });
    }, [validate, promptObjetivos]);

    const modifyObjetives = useCallback((option) => {
        const dataForm = getValues();
        if (!dataForm?.ideas) return methods?.setError("ideas", {
            type: "manual",
            message: t("campo_requerido"),
        });
        validate({ forceObjective: true, onSuccess: _onSuccess({ othres: { index: option.index, edited: option?.option?.edited } }), typeprompt: "modificar_objetivos", _promptObjetivos: promptobjetivosmodificar({ ideas: dataForm.ideas, option: option?.option?.option?.[0], current: option?.option?.response }) });
    }, [validate, promptobjetivosmodificar, getValues, t, methods, _onSuccess]);

    const resetOptions = useCallback(() => {
        setOptions([]);
        setHeaders([]);
        setOptionModify(null);
    }, []);

    const onClickHaveAnotherOption = useCallback(() => {
        resetOptions();
        setHaveNewOptions(true);
    }, [resetOptions]);

    const removeobjetivesOptions = useCallback((id) => {
        let index = objetivesOptions.findIndex((item) => item.id === id);
        if (index >= 0) remove(index);
    }, [remove, objetivesOptions]);

    return { mutation, onClickHaveAnotherOption, haveNewOptions, headers, options, modifyObjetives, setOptionModify, optionModify, loading: mutation.isLoading, validate, onClickGenerateNewOptions, t, resetOptions, objetivesOptions, removeobjetivesOptions };
};


/////////////ENAD///////////////////////////


/////////////BC///////////////////////
/////////////BC///////////////////////

export const useMutationGetClassProfile = ({ onsuccess = null, config, dataToFormDatafn } = {}) => {
    const [dataForm, setDataForm] = useState();
    const { config: _config } = useSwitchInstitution({ config });
    const _onSuccess = useCallback((data) => {
        setDataForm(dataToFormDatafn(data, { config: _config?.options }));
    }, [_config, dataToFormDatafn]);
    const { mutate, isLoading, isError } = useMutation(getClassProfileById, {
        onSuccess: onsuccess ? onsuccess : _onSuccess,
        onError: (error) => { },
    });
    return { mutate, isLoading, dataForm, isError, config: _config };
}

/**
 * Permite enviar la información a el back-end 
 * @returns 
 */
export const useMutationCourse = ({
    onsuccess = null,
    defaultreadonly = false,
    mutationFn = storeClassProfileJson,
    createbody,
    useid = false } = {}) => {
    const [readonly, setReadOnly] = useState(defaultreadonly);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useSessionStore();
    const { show } = useSnackbarStore();
    const changeReadOnly = useCallback((value) => {
        setReadOnly((_value) => {
            if (value === undefined) return !_value;
            return value;
        });
    }, []);

    const { isLoading, mutate } = useMutation(mutationFn, {
        onSuccess: onsuccess ? onsuccess({
            show: (key) => show(t(key)),
            changeReadOnly
        }) : (data) => {
            show(t("group_created"));
            navigate("/course-profile");
        }
    });
    const send = useCallback((data) => {
        if (isLoading) return null;
        let body = createbody({ data, user });
        if (useid) mutate({ id: data.id, body });
        else mutate(body);
    }, [isLoading, mutate, user, useid, createbody]);

    return { loading: isLoading, t, send, readonly, changeReadOnly }
}


/**
 * Permite interactuar con las opciones de navegacion
 * @returns 
 */
export const useUtilsNavigation = ({ menu = [] } = {}) => {
    const { type, id } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Opciones para seleccionar
    const options = useMemo(() => {
        if (!menu?.length) return [];
        return menu?.map((item) => ({
            title: t(item.title),
            description: t(item.description),
            customColorTitle: item.customColorTitle,
            type: item.type
        }))
    }, [t, menu]);
    const currentOption = useMemo(() => options.find(option => option.type === type), [options, type]);
    return { type, t, options, navigate, currentOption, language: i18n.language, id };
}

/**
 * Permite interactuar con los componnetes de nivel
 * @param {*} param0 
 * @returns 
 */
export const useLevelCard = ({ option, _iconslevels }) => {
    const { t } = useTranslation();

    const levels = useMemo(() => {
        let icon = option?.icon?.split(',') ?? [];
        icon = icon.map((key) => _iconslevels[key]);
        const _levels = option?.nivel?.split('+') ?? [];
        return _levels.map((level, index) => ({ level: ` ${index % 2 ? "+" : ""} ${level}`, color: icon?.[index]?.color ?? icon?.[0]?.color }));
    }, [option, _iconslevels]);

    const images = useMemo(() => {
        const icon = option?.icon?.split(',') ?? [];
        return icon.map((key) => ({ icon: _iconslevels[key].icon, alt: _iconslevels[key].alt }));
    }, [option, _iconslevels]);

    return { levels, t, images };
}

/**
 * Este Hook agrpa las funcionalidades del paso 2
 * permitiiendo la interaccion con el LLM
 * @param {*} param0 
 * @returns 
*/
export const useFormGroupObjectives = ({ arrayFields, methods }) => {
    const { t } = useTranslation();
    const { fields: objetivesOptions, remove, append } = arrayFields.fieldArrayObjetivesOptions;
    const objectiveType = methods.watch("objectiveType");
    const { setValue,clearErrors } = methods;

    useEffect(() => {
        setValue("objectiveinput", null);
        clearErrors("objectiveinput");
    }, [objectiveType, setValue,clearErrors])

    const removeobjetivesOptions = useCallback((id) => {
        let index = objetivesOptions.findIndex((item) => item.id === id);
        if (index >= 0) remove(index);
    }, [remove, objetivesOptions]);

    const addObjectives = useCallback((option) => {
        append({ id: uuidv4(), ...option });
    }, [append]);

    return { removeobjetivesOptions, addObjectives, t, objetivesOptions };
};

export const useDBAdata = ({methods}) => {
    const { watch } = methods;
    const [dbaoptions, setDbaoptions] = useState([]);
    const knowledge_area = watch("knowledge_area");
    const grade = watch("grade");
    const {isLoading} = useQuery(["dbadata",grade?.value??0,knowledge_area?.value??0], async () => await getbygradearea({grade:grade?.value??0,area:knowledge_area?.value??0}),{
        enabled:!!grade?.value&&!!knowledge_area?.value,
        cacheTime: 60*5*1000,
        onSuccess: (data) => {
            if(data) setDbaoptions(data?.map?.((option) => ({ ...option, value: option.id, label: option.standard })));
        }
    });
    return {dbaoptions,isLoading};
}

/**
 * Hook para obtener las competencias y grados
 * @returns 
 */
export const useSubjectAndGradesCompetences = () => {
    const { user } = useSessionStore();
    const [grades, setGrades] = useState([]);
    const [subjecAreas, setSubjecAreas] = useState([]);

    const { isLoading:isLoadingsubjectAreas, isError:isErrorsubjectAreas } = useQuery(
        ["subjectAreas", user?.email],
        async () => await getAllSubjectAreasByUser(user?.email),
        {
            onSuccess: (data) => {
                if (data) {
                    const currentSubjectAreas = data
                        .filter((e) => e.name !== "Competencias transversales")
                        .map((e) => ({ label: e.name, value: e.id }));
                    setSubjecAreas(currentSubjectAreas);
                }
            },
            enabled: !!user?.email,
            cacheTime: 60*5*1000,
        }
    );

    const { isLoading:isLoadingGradeLevels, isError:isErrorGradeLevels } = useQuery(
        ["GradeLevels", user?.email],
        async () => await getAllGradeLevelsByUser(user?.email),
        {
            onSuccess: (data) => {
                if (data) {
                    const currentGrades = data.map((e) => ({
                        label: e.name,
                        value: e.id,
                    }));
                    setGrades(currentGrades);
                }
            },
            enabled: !!user?.email,
            cacheTime: 60*5*1000,
        }
    );
    return { grades, subjecAreas, isLoading:isLoadingGradeLevels||isLoadingsubjectAreas, isError:isErrorGradeLevels||isErrorsubjectAreas };
}


export const useGeneralFormT1 = ({ methods }) => {
    const { t } = useTranslation();

    const { grades, subjecAreas, isLoading, isError } = useSubjectAndGradesCompetences();

    //se agrega la institucion si no existe 
    const {watch,setValue}= methods;
    const { institution} = useSwitchInstitution({ config: {} });
    const institutionForm = watch("institution");
    useEffect(() => {
        if(!institutionForm?.value && institution?.id)setValue("institution",{value:institution?.id,label:institution?.name});
    }, [institution,institutionForm,setValue]);

    return { grades, subjecAreas, isLoading, isError, t };
}